import { useState, useEffect, useMemo } from "react";
import { Form, Typography } from "antd";
import { useLocation } from "react-router-dom";
import "./roles.css";
import secureLocalStorage from "react-secure-storage";

import CustomLoader from "../customLoader";
import TableContent from "../../../components/atoms/TableContent";
import TopContainerHeader from "../../../components/molecules/TopComponentHeader";
import AddRole from "./addRole";
import media from "../../../media";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  fetchRolesThunk,
  fetchSectionsAsDropdownThunk,
} from "../../../store/thunks/rolesThunk";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import Unauthorized from "../../layouts/Unauthorized";

const Roles = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const currentPath = location.pathname.replace("/", "");
  const [tableForm] = Form.useForm();
  const rawAccess = useSelector((state) => state.auth.access);
  const { list, pageInfo, count, loading } = useSelector(
    (state) => state.muRoles,
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE || 30),
  });
  const shouldShowPagination = count > pagination.pageSize;
  const tablePagination = shouldShowPagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        locale: { items_per_page: "" },
        onChange: (page, pageSize) => {
          setPagination({ current: page, pageSize });
        },
      }
    : false;

  //States
  const [sections, setSections] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [statuses, setStatuses] = useState([
    { label: "ACTIVE", value: "ACTIVE" },
    { label: "INACTIVE", value: "INACTIVE" },
  ]);

  const [editingKey, setEditingKey] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);

  const hasViewAccess = hasPermission(accessMap, "roles", "view");
  const openAddModal = () => {
    setEditRecord(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const additionalColumns = [];
  const columns = [
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.auditId.svg} alt="icon" />
            ID
          </div>
        );
      },
      dataIndex: "id",
      render: (text) => <span className="rowstyle">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentIcon.svg} alt="icon" />
            Name
          </div>
        );
      },
      dataIndex: "name",
      editable: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, row) => <span className="rowstyle">{text}</span>,
    },
    ,
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.auditId.svg} alt="icon" />
            Sections
          </div>
        );
      },
      dataIndex: "sections",
      editable: true,
      render: (sectionIds = []) => {
        if (!Array.isArray(sectionIds) || sectionIds.length === 0) {
          return "-";
        }

        const labels = sectionIds
          .map((id) => sections.find((p) => p.value === id)?.label)
          .filter(Boolean)
          .join(", ");

        const maxLength = 50;
        const truncatedText =
          labels && labels.length > maxLength
            ? labels.substring(0, maxLength) + "..."
            : labels;
        return (
          <span className="rowstyle" title={labels}>
            {truncatedText}
          </span>
        );
      },
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentStatus.svg} alt="icon" />
            Status
          </div>
        );
      },
      dataIndex: "status",
      editable: true,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text, row) => (
        <span
          className={`cusStatus ${
            text?.toLowerCase() === "active" ? "cusActive" : "cusInActive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentDate.svg} alt="icon" />
            Created At
          </div>
        );
      },
      dataIndex: "createdAt",
      render: (text) => <span className="rowstyle">{text}</span>,
    },
  ];
  additionalColumns.push(
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentDate.svg} alt="icon" />
            Updated At
          </div>
        );
      },
      dataIndex: "updatedAt",
      render: (text) => <span className="rowstyle ">{text || "-"}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentAction.svg} alt="icon" />
            Action
          </div>
        );
      },
      dataIndex: "action",

      render: (_, record) => {
        return (
          hasPermission(accessMap, "roles", "edit") && (
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => openEditModal(record)}
              className="tableBtn bfont"
            >
              <i className="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
            </Typography.Link>
          )
        );
      },
    },
  );
  columns.push(...additionalColumns);
  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          editing: isEditing(record) ? true : undefined,
          cellconfig: {
            inputType: "text",
            dataIndex: col.dataIndex,
            title: typeof col.title === "string" ? col.title : undefined,
            statuses,
            sections,
          },
        }),
      };
    });
  const [selectionType, setSelectionType] = useState("checkbox");

  /*begin::getUserList will fetch list of users*/

  // Fetch roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        setShowLoader(true);
        const hasSearchedValue = secureLocalStorage.getItem("hasSearchedValue")
          ? "yes"
          : "";
        const searchKeyword = secureLocalStorage.getItem("searchKeyword") || "";

        const res = await dispatch(
          fetchRolesThunk({
            searchIn: currentPath,
            callActionFrom: currentPath,
            hasSearchedValue,
            searchKeyword,
            route: currentPath,
            pagination: {
              current: pagination.current,
              page: pagination.current,
              pageSize: pagination.pageSize,
            },
            listing: "yes",
          }),
        ).unwrap();
      } catch (err) {
        console.error("Error fetching permissions:", err);
      } finally {
        setShowLoader(false);
      }
    };

    fetchData();
  }, [currentPath, pagination.current, pagination.pageSize, dispatch]);

  // Fetch sections
  const getSectionsList = async () => {
    const payload = { route: "sections", action: "findAll" };

    await dispatch(fetchSectionsAsDropdownThunk(payload))
      .unwrap()
      .then((response) => {
        const temp = [];
        response?.map((section, index) => {
          temp.push({ label: section?.name, value: section?._id });
        });
        setSections(temp);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    getSectionsList();
    //getRolesList();
  }, []);

  /*end::getUserList will fetch list of users*/

  const isEditing = (record) => record.key === editingKey;

  if (!hasViewAccess) {
    return <Unauthorized />;
  }
  return (
    <div className="all-content">
      <CustomLoader showLoader={loading} />
      <TopContainerHeader
        sectionTitle="Roles"
        sectionDescription={showLoader ? "Loading..." : `${count || 0} Found`}
        hasSectionButton={hasPermission(accessMap, "roles", "add") && true}
        sectionButtons={
          hasPermission(accessMap, "roles", "add") && [
            {
              isVisible: true,
              itemType: "add",
              itemName: "Add",
              onClick: openAddModal,
            },
          ]
        }
        hasFilter={false}
        buttonTitle="Add New"
        FormComponent={AddRole}
        formTitle={editRecord ? "Edit Role" : "Add Role"}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        formProps={{
          editData: editRecord,
          statuses,
          sections,
          onSuccess: () => {
            dispatch(
              fetchRolesThunk({
                searchIn: currentPath,
                callActionFrom: currentPath,
                hasSearchedValue: secureLocalStorage.getItem("hasSearchedValue")
                  ? "yes"
                  : "",
                searchKeyword:
                  secureLocalStorage.getItem("searchKeyword") || "",
                route: currentPath,
                pagination: {
                  current: pagination.current,
                  page: pagination.current,
                  pageSize: pagination.pageSize,
                },
              }),
            );
            setModalOpen(false);
          },
        }}
      />

      <div className="all-table-holder">
        <Form form={tableForm} component={false}>
          <TableContent
            rowClassName="editable-row"
            bordered={false}
            columns={mergedColumns}
            dataSource={list}
            pagination={tablePagination}
          />
        </Form>
      </div>
    </div>
  );
};
export default Roles;
